import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import vaultService from '../services/vaultService';
import syncService from '../services/syncService';
import paymentporterService from '../services/paymentporterService';
import './Dashboard.css';
import SupportTicketsPanel from './support/SupportTicketsPanel';

// ─── Vault constants ────────────────────────────────────────────────────────
const VAULT_STATUS_LABELS = {
  initiated: 'Initiated',
  connection_successful: 'Connected',
  client_generated: 'Client Generated',
  client_executed: 'Client Executed',
  records_extracted: 'Records Extracted',
  records_processed: 'Records Processed',
  vault_tables_created: 'Tables Created',
  vault_ready: 'Ready',
};
const VAULT_STATUS_COLOR = {
  vault_ready: '#22c55e',
  records_processed: '#3b82f6',
  vault_tables_created: '#3b82f6',
  records_extracted: '#f59e0b',
  client_executed: '#f59e0b',
  client_generated: '#f59e0b',
  connection_successful: '#8b5cf6',
  initiated: '#9ca3af',
};

// ─── Sync constants ──────────────────────────────────────────────────────────
const SYNC_STATUS_COLOR = {
  RUNNING: '#22c55e',
  STOPPED: '#ef4444',
  ERROR: '#ef4444',
  PENDING: '#f59e0b',
  COMPLETE: '#3b82f6',
  COMPLETED: '#3b82f6',
};
const syncStatusLabel = (s) => {
  const u = s?.toUpperCase();
  if (u === 'RUNNING') return 'Active';
  if (u === 'COMPLETE' || u === 'COMPLETED') return 'Completed';
  return s || 'Unknown';
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function ProductCard({ title, icon, color, children, onViewAll, viewAllLabel = 'View all' }) {
  return (
    <div className="dash-product-card">
      <div className="dash-product-header">
        <div className="dash-product-icon" style={{ background: color + '18', color }}>
          {icon}
        </div>
        <h2 className="dash-product-title">{title}</h2>
        <button className="dash-product-link" onClick={onViewAll}>{viewAllLabel} →</button>
      </div>
      {children}
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="dash-mini-stat">
      <div className="dash-mini-value" style={{ color }}>{value === null ? '—' : value}</div>
      <div className="dash-mini-label">{label}</div>
    </div>
  );
}

function StatusBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="dash-sbar-row">
      <span className="dash-sbar-dot" style={{ background: color }} />
      <span className="dash-sbar-name">{label}</span>
      <div className="dash-sbar-track">
        <div className="dash-sbar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="dash-sbar-count">{count}</span>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin, partners: memberPartners } = useAuth();
  // The PARTNER's ticket queue is for its own people. Somebody who picks a partner in the navbar without being a
  // member of it (a person at one of its vendors) is refused it by the server (404) — they see their own tickets.
  const partnerQueue = (pid) => !!pid && (isSuperAdmin
    || (memberPartners || []).some(p => Number(p.partnerId) === Number(pid)));
  const { selectedAccount, selectedPartner, selectedHotel } = useAccount();

  const [vaults, setVaults] = useState([]);
  const [syncProjects, setSyncProjects] = useState([]);
  const [porters, setPorters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const vaultFilters = {};
      if (selectedAccount) vaultFilters.accountid = selectedAccount;
      else if (selectedPartner) vaultFilters.partnerid = selectedPartner;

      const ppFilters = {};
      if (selectedAccount) ppFilters.account_id = selectedAccount;
      else if (selectedPartner) ppFilters.partner_id = selectedPartner;

      const [vaultRes, syncRes, ppRes] = await Promise.allSettled([
        vaultService.getAllVaults(vaultFilters),
        syncService.getSyncProjects(selectedAccount, selectedPartner),
        paymentporterService.list(ppFilters),
      ]);

      if (cancelled) return;

      setVaults(vaultRes.status === 'fulfilled' ? (vaultRes.value.vaults || []) : []);
      setSyncProjects(syncRes.status === 'fulfilled' ? (syncRes.value || []) : []);
      setPorters(ppRes.status === 'fulfilled' ? (ppRes.data?.paymentPorters || []) : []);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [selectedAccount, selectedPartner]);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.given_name || user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'there';

  // ── Vault derived data ─────────────────────────────────────────────────────
  const vaultStatusCounts = vaults.reduce((acc, v) => {
    const s = v.status || 'initiated';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const readyVaults = vaultStatusCounts['vault_ready'] || 0;
  const recentVaults = [...vaults]
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
    .slice(0, 6);

  // ── Sync derived data ──────────────────────────────────────────────────────
  const syncStatusCounts = syncProjects.reduce((acc, p) => {
    const s = (p.projectStatus || 'UNKNOWN').toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const runningSyncs = syncStatusCounts['RUNNING'] || 0;
  const recentSyncs = [...syncProjects]
    .sort((a, b) => new Date(b.updatedAt || b.updated_at || 0) - new Date(a.updatedAt || a.updated_at || 0))
    .slice(0, 6);

  // ── Apps/PP derived data ───────────────────────────────────────────────────
  const activePorters = porters.filter(p => p.active === 'Y' || p.active === 1 || p.active === true).length;
  const recentPorters = [...porters]
    .sort((a, b) => new Date(b.updated_at || b.updatedAt || 0) - new Date(a.updated_at || a.updatedAt || 0))
    .slice(0, 6);

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-greeting">{greeting()}, {firstName}</h1>
          <p className="dash-date">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Product panels */}
      <div className="dash-products-grid">

        {/* ── Vault ── */}
        <ProductCard
          title="Vault"
          color="#f59e0b"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="2" width="20" height="20" rx="3" stroke="currentColor" strokeWidth="2"/>
              <rect x="7" y="7" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="2"/>
              <circle cx="12" cy="12" r="2" fill="currentColor"/>
            </svg>
          }
          onViewAll={() => navigate('/vaults')}
        >
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : (
            <>
              <div className="dash-mini-stats-row">
                <MiniStat label="Total" value={vaults.length} color="#f59e0b" />
                <MiniStat label="Ready" value={readyVaults} color="#22c55e" />
                <MiniStat label="In progress" value={vaults.length - readyVaults} color="#8b5cf6" />
              </div>
              {vaults.length > 0 && (
                <div className="dash-status-bars">
                  {Object.entries(vaultStatusCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([status, count]) => (
                      <StatusBar
                        key={status}
                        label={VAULT_STATUS_LABELS[status] || status}
                        count={count}
                        total={vaults.length}
                        color={VAULT_STATUS_COLOR[status] || '#9ca3af'}
                      />
                    ))}
                </div>
              )}
              {vaults.length === 0 && (
                <p className="dash-panel-empty">No vaults yet.</p>
              )}
            </>
          )}
        </ProductCard>

        {/* ── Sync ── */}
        <ProductCard
          title="Sync"
          color="#06b6d4"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M4 12a8 8 0 018-8v2m8 6a8 8 0 01-8 8v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M20 4l-2 2m2-2l-2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 20l2-2m-2 2l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          onViewAll={() => navigate('/sync')}
        >
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : (
            <>
              <div className="dash-mini-stats-row">
                <MiniStat label="Projects" value={syncProjects.length} color="#06b6d4" />
                <MiniStat label="Running" value={runningSyncs} color="#22c55e" />
                <MiniStat label="Stopped/Error" value={(syncStatusCounts['STOPPED'] || 0) + (syncStatusCounts['ERROR'] || 0)} color="#ef4444" />
              </div>
              {syncProjects.length > 0 && (
                <div className="dash-status-bars">
                  {Object.entries(syncStatusCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([status, count]) => (
                      <StatusBar
                        key={status}
                        label={syncStatusLabel(status)}
                        count={count}
                        total={syncProjects.length}
                        color={SYNC_STATUS_COLOR[status] || '#9ca3af'}
                      />
                    ))}
                </div>
              )}
              {syncProjects.length === 0 && (
                <p className="dash-panel-empty">No sync projects yet.</p>
              )}
            </>
          )}
        </ProductCard>

        {/* ── Apps (Payment Porter) ── */}
        <ProductCard
          title="Apps"
          color="#4F7FFF"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
              <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
              <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2"/>
            </svg>
          }
          onViewAll={() => navigate('/apps')}
        >
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : (
            <>
              <div className="dash-mini-stats-row">
                <MiniStat label="Payment Porters" value={porters.length} color="#4F7FFF" />
                <MiniStat label="Active" value={activePorters} color="#22c55e" />
                <MiniStat label="Inactive" value={porters.length - activePorters} color="#9ca3af" />
              </div>
              {porters.length > 0 ? (
                <div className="dash-status-bars">
                  <StatusBar label="Active" count={activePorters} total={porters.length} color="#22c55e" />
                  <StatusBar label="Inactive" count={porters.length - activePorters} total={porters.length} color="#9ca3af" />
                </div>
              ) : (
                <p className="dash-panel-empty">No payment porters yet.</p>
              )}
            </>
          )}
        </ProductCard>
      </div>

      {/* Open support tickets for what the navbar has chosen — the hotel, else the chain (and its hotels), else the
          partner (members only — see partnerQueue); otherwise the tickets this user raised. The server checks each scope. */}
      <div className="dash-support">
        <div className="dash-recent-header">
          <h3 className="dash-recent-title">Open support tickets</h3>
          <button className="dash-recent-link" onClick={() => navigate('/support')}>View all</button>
        </div>
        <SupportTicketsPanel openOnly limit={10}
          {...(selectedHotel ? { scope: 'hotel', hotelId: selectedHotel }
            : selectedAccount ? { scope: 'account', accountId: selectedAccount }
              : partnerQueue(selectedPartner) ? { scope: 'partner', partnerId: selectedPartner } : { scope: 'mine' })}
          emptyText={selectedHotel ? 'No open tickets for this hotel' : selectedAccount ? 'No open tickets for this chain'
            : partnerQueue(selectedPartner) ? 'No open tickets for this partner' : 'You have no open tickets'} />
      </div>

      {/* Recent activity — three columns */}
      <div className="dash-recent-grid">

        {/* Recent vaults */}
        <div className="dash-recent-panel">
          <div className="dash-recent-header">
            <h3 className="dash-recent-title">Recent Vaults</h3>
            <button className="dash-recent-link" onClick={() => navigate('/vaults')}>View all</button>
          </div>
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : recentVaults.length === 0 ? (
            <p className="dash-panel-empty">No vaults yet.</p>
          ) : (
            <div className="dash-item-list">
              {recentVaults.map(vault => {
                const color = VAULT_STATUS_COLOR[vault.status] || '#9ca3af';
                return (
                  <div key={vault.id} className="dash-item-row" onClick={() => navigate(`/vault/${vault.id}`)}>
                    <div className="dash-item-info">
                      <span className="dash-item-name">{vault.name || `Vault ${vault.id}`}</span>
                      <span className="dash-item-sub">
                        {vault.updated_at
                          ? new Date(vault.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          : ''}
                      </span>
                    </div>
                    <span className="dash-item-badge" style={{ background: color + '18', color }}>
                      {VAULT_STATUS_LABELS[vault.status] || vault.status || 'initiated'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent sync projects */}
        <div className="dash-recent-panel">
          <div className="dash-recent-header">
            <h3 className="dash-recent-title">Recent Sync Projects</h3>
            <button className="dash-recent-link" onClick={() => navigate('/sync')}>View all</button>
          </div>
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : recentSyncs.length === 0 ? (
            <p className="dash-panel-empty">No sync projects yet.</p>
          ) : (
            <div className="dash-item-list">
              {recentSyncs.map(project => {
                const status = (project.projectStatus || 'UNKNOWN').toUpperCase();
                const color = SYNC_STATUS_COLOR[status] || '#9ca3af';
                // Addressed by syncid, like every other link to a project (SyncPage, the project form).
                // The API resolves either form, but one identifier in the address bar means a link can be
                // read, quoted and compared against the database without a lookup.
                return (
                  <div key={project.syncuuid} className="dash-item-row" onClick={() => navigate(`/sync/${project.syncId || project.syncuuid}`)}>
                    <div className="dash-item-info">
                      <span className="dash-item-name">{project.projectName || project.syncuuid}</span>
                      <span className="dash-item-sub">{project.templateName || ''}</span>
                    </div>
                    <span className="dash-item-badge" style={{ background: color + '18', color }}>
                      {syncStatusLabel(project.projectStatus)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent payment porters */}
        <div className="dash-recent-panel">
          <div className="dash-recent-header">
            <h3 className="dash-recent-title">Recent Payment Porters</h3>
            <button className="dash-recent-link" onClick={() => navigate('/apps')}>View all</button>
          </div>
          {loading ? (
            <div className="dash-panel-loading">Loading…</div>
          ) : recentPorters.length === 0 ? (
            <p className="dash-panel-empty">No payment porters yet.</p>
          ) : (
            <div className="dash-item-list">
              {recentPorters.map(p => {
                const isActive = p.active === 'Y' || p.active === 1 || p.active === true;
                return (
                  <div key={p.id} className="dash-item-row" onClick={() => navigate(`/apps/payment-porter/${p.id}`)}>
                    <div className="dash-item-info">
                      <span className="dash-item-name">{p.name || `Porter ${p.id}`}</span>
                      <span className="dash-item-sub">{p.hotelName || p.hotel_name || ''}</span>
                    </div>
                    <span className="dash-item-badge" style={isActive ? { background: '#dcfce7', color: '#15803d' } : { background: '#f3f4f6', color: '#6b7280' }}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
